<template lang="pug">
  .b-history
    FormBlock(mobile v-if="attachedFiles.length")._attached-files Закрепленные файлы
      FileList(
        :files="attachedFiles"
        downloadAll
        zoom
        :attached="attachedFiles"
        :attachable="attachable"
        @attach="$emit('attach', $event)"
        @detach="$emit('detach', $event)"
      )
    ._fetch-more(v-if="!allFetched")
      Btn(@click="fetchAllHistory" grey transparent sm) Загрузить всю историю
        NotificationCount(:count="notificationCount")
        Icon(name="angle-right")
    FormBlock._block(v-if="history.length" mobile)
      template(v-for="item in history")
        TaskDetailHistoryBlock._history-block(v-if="Array.isArray(item)" :items="item" :key="item[0].id")
        ._item(v-if="item.type === 'comment'" :key="item.id")
          CommentForm(
            v-if="editableComment && editableComment.id === item.id"
            :comment="editableComment"
            @cancel="cancelCommentUpdate"
            @submit="updatePublishedComment"
            @scrollToComment="scrollToComment"
          )
            template(#files)
              ProjectFileUploader(:selected="editableComment.projectFiles" @select="onPublishedCommentFilesUpdate")
          CommentTile(
            v-else
            :comment="item"
            :taskId="task.id"
            :ref="item['@id']"
            :editable="allowToEditComments && !item.agreement"
            :extraMenuItems="getExtraMenuItems(item)"
            @edit="editPublishedComment(item)"
            @reply="$emit('reply', item)"
            @delete="deleteComment(item)"
            @scrollToComment="scrollToComment"
          )
            template(#files v-if="item.projectFiles.length")
              FileList(
                :files="item.projectFiles"
                downloadAll
                zoom
                :attached="attachedFiles"
                :attachable="attachable"
                @attach="$emit('attach', $event)"
                @detach="$emit('detach', $event)"
              )
            template(#footer v-if="item.agreement")
              AgreementTile(:item="item.agreement" @deleted="agreementDeleted(item)")
                template(#files v-if="item.agreement.answeredAt && item.agreement.projectFiles.length")
                  FileList._files(
                    :files="item.agreement.projectFiles"
                    downloadAll
                    zoom
                    :attached="attachedFiles"
                    :attachable="attachable"
                    @attach="$emit('attach', $event)"
                    @detach="$emit('detach', $event)"
                  )
</template>

<script>
import FormBlock from '@/components/FormBlock.vue';
import CommentForm from '@/components/CommentForm.vue';
import CommentTile from '@/components/CommentTile.vue';
import NotificationCount from '@/components/base/NotificationCount.vue';
import TaskDetailHistoryBlock from '@/components/task/detail/TaskDetailHistoryBlock.vue';
import ProjectFileUploader from '@/components/ProjectFileUploader.vue';
import AgreementTile from '@/components/task/detail/AgreementTile.vue';
import FileList from '@/components/FileList.vue';
import ExternalContactListPopupWrappable from '@/components/agreement/ExternalContactListPopupWrappable.vue';

import taskChangeApi from '@/api/taskChange';
import agreementApi from '@/api/agreement';
import popup from '@/model/popup';

export default {
  name: 'TaskDetailHistory',
  components: { FileList, AgreementTile, TaskDetailHistoryBlock, FormBlock, CommentTile, CommentForm, ProjectFileUploader, NotificationCount }, // eslint-disable-line
  props: {
    allowToEditComments: Boolean,
    project: Object,
    task: Object,
    access: Object,
    attachable: Boolean,
    attachedFiles: Array,
  },
  data() {
    return {
      items: [],
      allFetched: false,
      editableComment: null,
    };
  },
  async created() {
    this.loadFirstComments();
  },
  watch: {
    $route(val) {
      this.$nextTick(() => { this.scrollToComment(`/api/task_changes/${val.query.comment}`); });
    },
  },
  computed: {
    ids() {
      return this.items.map(i => i.id);
    },
    history() {
      return this.items
        .map((item) => ({ ...item, createdAt: new Date(item.createdAt) }))
        .sort((a, b) => a.createdAt - b.createdAt)
        .reduce((acc, item) => {
          if (item.type === 'comment') {
            return [...acc, item];
          }

          if (acc.length <= 0) {
            return [[item]];
          }

          const last = acc.pop();
          return Array.isArray(last)
            ? [...acc, [...last, item]]
            : [...acc, last, [item]];
        }, []);
    },
    notificationCount() {
      const getter = this.$store.getters['notification/itemIdsByFilter'];
      const notifications = getter({ taskId: this.task.id });
      return notifications.filter(n => !!n.info.commentId && !this.ids.includes(n.info.commentId)).length;
    },
  },
  methods: {
    async loadFirstComments() {
      const filter = {
        task: this.task.id,
        itemsPerPage: 5,
        type: 'comment',
        isDraft: false,
      };

      const r = await taskChangeApi.fetchList(filter, true);
      this.addNewItems(r.items);
      this.loadFirstHistory();

      const commentId = this.$route.query?.comment;

      if (!commentId) return;

      const commentFetched = this.items.find(i => i.id === +commentId);
      if (commentFetched) {
        this.$nextTick(() => { this.scrollToComment(`/api/task_changes/${commentId}`); });
      } else {
        this.fetchAllHistory(commentId);
      }
    },
    async loadFirstHistory() {
      const date = this.items.slice(-1).pop()?.createdAt;

      const defaultFilter = { task: this.task.id, isDraft: false };
      const filter = date
        ? { ...defaultFilter, pagination: false, 'createdAt[after]': date }
        : { ...defaultFilter, itemsPerPage: 10 };

      const r = await taskChangeApi.fetchList(filter, true);
      this.addNewItems(r.items);
      this.fetchNewChanges();
    },
    async fetchAllHistory(commentId) {
      const filter = {
        task: this.task.id,
        pagination: false,
        isDraft: false,
      };

      const r = await taskChangeApi.fetchList(filter, true);
      this.allFetched = true;
      this.items = r.items;

      if (commentId) {
        this.$nextTick(() => { this.scrollToComment(`/api/task_changes/${commentId}`); });
      }
    },
    fetchNewChanges() {
      const loadChanges = () => {
        if (document.hidden) return;
        taskChangeApi
          .fetchList({ task: this.task.id, itemsPerPage: 5, isDraft: false })
          .then((r) => { this.addNewItems(r.items); });
      };

      const loadAgreements = () => {
        if (document.hidden) return;
        agreementApi
          .fetchList({ 'comment.task': this.task.id })
          .then(r => { this.applyAgreementChanges(r.items) });
      };

      setInterval(loadChanges, 30000);
      setInterval(loadAgreements, 60000);
    },
    applyAgreementChanges(arr) {
      const agreementsToUpdate = Object.fromEntries(
        arr
          .filter(a => this.ids.includes(a.comment.id))
          .map(a => [a.comment.id, a])
      );

      for (let i = 0; i < this.items.length; i++) {
        const item = this.items[i];
        const newAgreement = agreementsToUpdate[item.id];
        if (item.agreement && item.agreement?.isAgreed !== newAgreement?.isAgreed) {
          item.agreement = newAgreement;
        }
      }
    },
    addNewItems(arr) {
      const newItems = arr.filter(i => !this.ids.includes(i.id));

      if (newItems.length) {
        this.items = [...this.items, ...newItems];
      }
    },
    extAdd(item) {
      this.items.push(item);
    },
    restoreProjectFiles(projectFiles) {
      const filesInStore = this.$store.state.projectFile.files[this.project.id];
      return projectFiles
        .map((iri) => filesInStore.find((file) => file['@id'] === iri))
        .map((item) => ({ ...item, state: 'static' }));
    },
    scrollToComment(ref) {
      this.$refs[ref]?.[0]?.$el?.scrollIntoView({ behavior: 'smooth' }); // eslint-disable-line no-unused-expressions
    },
    async deleteComment(comment) {
      if (await this.$confirm('Вы действительно хотите удалить комментарий?')) {
        taskChangeApi.delete(comment.id, true)
          .then(() => { this.items = this.items.filter((item) => item.id !== comment.id); })
          .catch(() => { this.$alert('При удалении произошла ошибка'); });
      }
    },

    editPublishedComment(comment) {
      this.editableComment = comment;
      this.editableComment.projectFiles = comment.projectFiles.map((item) => item['@id']);
      this.$store.commit('closePrevent/enable');

      // this.editableComment = {
      //   ...comment,
      //   projectFiles: comment.projectFiles.map((item) => item['@id']),
      // };
    },
    onPublishedCommentFilesUpdate(files) {
      this.editableComment.projectFiles = files;
    },
    updatePublishedComment({ id, info: { text } }) {
      const { projectFiles } = this.editableComment;
      const editableCommentBkp = this.editableComment;

      // this.projectFiles.filter((i) => i) ?? [], // wtf
      // todo replyTo ?

      // const commentInHistory = this.history.find((item) => item.id === id);
      // commentInHistory.info.text = text;
      // commentInHistory.projectFiles = this.restoreProjectFiles(projectFiles);

      taskChangeApi.update(id, { text, projectFiles }, true)
        .then((r) => {
          this.editableComment.info.text = text;
          this.editableComment.projectFiles = this.restoreProjectFiles(projectFiles);
          this.editableComment.updatedAt = r.updatedAt;
          this.editableComment = null;
          this.$store.commit('closePrevent/disable');
        })
        .catch(() => {
          this.$alert('При сохранении произошла ошибка');
          this.editableComment = editableCommentBkp;
          this.editableComment.projectFiles = projectFiles;
        });
    },
    cancelCommentUpdate() {
      this.editableComment.projectFiles = this.restoreProjectFiles(this.editableComment.projectFiles);
      this.editableComment = null;
      this.$store.commit('closePrevent/disable');
    },

    getExtraMenuItems(comment) {
      const items = [];

      if (!comment.agreement && (this.access.canUpdateTask())) {
        items.push({ text: 'Создать согласование', icon: 'user-check', handler: () => { this.createAgreement(comment); } });
      }

      if (this.access.canUpdateTask()) {
        items.push({ text: 'Закрепить все файлы', icon: 'attach-attach', handler: () => { this.attachAllFiles(comment); } });
        items.push({ text: 'Открепить все файлы', icon: 'attach-detach', handler: () => { this.detachAllFiles(comment); } });
      }

      return items;
    },
    async createAgreement(comment) {
      const contact = await popup.add({
        wrappable: true,
        component: ExternalContactListPopupWrappable,
        params: { project: this.project },
      });

      if (contact === false) return;

      const res = await agreementApi.create({ comment: comment['@id'], externalContact: contact }, true);
      const found = this.items.find(c => c.id === comment.id);
      delete res.comment;
      found.agreement = res;
    },
    agreementDeleted(comment) {
      const found = this.items.find(c => c.id === comment.id);
      found.agreement = null;
    },
    attachAllFiles(comment) {
      const alreadyAttached = this.attachedFiles.map(a => a.id);
      comment.projectFiles
        .forEach(pf => {
          if (alreadyAttached.includes(pf.id)) return;
          this.$emit('attach', pf);
        });
    },
    detachAllFiles(comment) {
      comment.projectFiles.forEach(pf => { this.$emit('detach', pf); });
    },
  },
};
</script>

<style scoped lang="scss">
.history {
  &__block {
    display: flex;
    flex-direction: column;
    gap: 40px;
    margin-bottom: 30px;
  }

  &__history-block {
     margin: -20px 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__attached-files {
    margin-bottom: 30px;
    display: flex;
    gap: 36px;
    flex-direction: column;
    font-size: 18px;
  }

  &__fetch-more {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 30px;

    ::v-deep .button.transparent {
      border-color: #D1D9F0 !important;
    }
    svg {
      fill: #8B8CA9;
    }

    &:before, &:after {
      display: block;
      content: '';
      height: 1px;
      background: #D1D9F0;
      flex-grow: 1;
    }
  }

}
</style>
